// src/auth/session.js
// Token storage, login/register/logout, automatic refresh and an authenticated fetch.
//
// - Access token: kept in memory only (lost on reload, re-created by a refresh).
// - Refresh token: kept in localStorage. "Logged in" means "has a refresh token".
// - Only one refresh runs at a time (per tab via a shared promise, across tabs via
//   navigator.locks), because the backend rotates refresh tokens and treats a
//   reused one as theft.

const RT_KEY = "refreshToken";
const DEVICE_KEY = "deviceId";
const BASE = (process.env.REACT_APP_AUTH_BASE || "").replace(/\/+$/, "");

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_PATTERN = /^\d{10}$/;
export const MIN_PASSWORD_LENGTH = 8; // keep equal to MIN_PASSWORD_LENGTH in auth-user

let accessToken = null;
let accessExp = 0; // ms timestamp
let inflight = null;
let onAuthLost = () => {};

export const setOnAuthLost = (fn) => {
  onAuthLost = typeof fn === "function" ? fn : () => {};
};

export const hasSession = () => {
  try {
    return !!localStorage.getItem(RT_KEY);
  } catch {
    return false;
  }
};

// If another tab logs out (or loses its session), this tab follows.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === RT_KEY && !e.newValue) {
      accessToken = null;
      accessExp = 0;
      onAuthLost();
    }
  });
}

function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

export function saveSession({ accessToken: at, refreshToken, expiresIn }) {
  accessToken = at;
  accessExp = Date.now() + Number(expiresIn || 900) * 1000;
  localStorage.setItem(RT_KEY, refreshToken);
}

export function clearSession() {
  accessToken = null;
  accessExp = 0;
  try {
    localStorage.removeItem(RT_KEY);
    localStorage.removeItem("authUser");
    sessionStorage.removeItem("sessionAuthed");
  } catch {}
}

// Turns what the user typed into the { type, value } the backend expects.
export function parseIdentifier(raw) {
  const value = String(raw || "").trim();
  if (value.includes("@")) {
    const email = value.toLowerCase();
    return EMAIL_PATTERN.test(email) ? { type: "email", value: email } : null;
  }
  return MOBILE_PATTERN.test(value) ? { type: "mobile", value } : null;
}

async function postJson(path, payload) {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  let json = null;
  try {
    json = await res.json();
  } catch {}
  return { res, json };
}

// ------------------------------------------------------------ login / register
// Both return { ok, message, user }. They never throw for normal failures.

export async function login(username, password) {
  const id = String(username || "").trim();
  const name = id.includes("@") ? id.toLowerCase() : id;
  try {
    const { res, json } = await postJson("/auth/login", {
      username: name,
      password,
      deviceId: getDeviceId(),
    });
    if (res.ok && json?.accessToken && json?.refreshToken) {
      saveSession(json);
      localStorage.setItem("authUser", name); // other pages still read this
      return { ok: true, message: "Login successful", user: json.data };
    }
    if (res.status === 401) return { ok: false, message: "Invalid username or password" };
    return { ok: false, message: json?.message || "Login failed" };
  } catch {
    return { ok: false, message: "Network error. Please try again." };
  }
}

export async function register({ identifier, password }) {
  const parsed = parseIdentifier(identifier);
  if (!parsed) {
    return { ok: false, message: "Enter a valid email or a 10-digit mobile number" };
  }
  const payload = {
    type: parsed.type,
    password,
    deviceId: getDeviceId(),
    [parsed.type === "email" ? "email" : "mobile"]: parsed.value,
  };
  try {
    const { res, json } = await postJson("/auth/register", payload);
    if (res.status === 200 || res.status === 201) {
      if (json?.accessToken && json?.refreshToken) {
        saveSession(json);
        localStorage.setItem("authUser", parsed.value);
        return { ok: true, loggedIn: true, message: "Registration successful", user: json.data };
      }
      return { ok: true, loggedIn: false, message: "Registration successful" };
    }
    if (res.status === 409) return { ok: false, message: json?.message || "User already exists" };
    return { ok: false, message: json?.message || "Registration failed" };
  } catch {
    return { ok: false, message: "Network error. Please try again." };
  }
}

// -------------------------------------------------------------------- refresh

async function doRefresh() {
  // Read inside the lock so we always use the newest token (another tab may have rotated it)
  const rt = localStorage.getItem(RT_KEY);
  if (!rt) {
    onAuthLost();
    throw new Error("no-session");
  }
  let res;
  try {
    res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: rt }),
    });
  } catch {
    throw new Error("refresh-unavailable"); // network problem: keep the session
  }
  if (res.status === 400 || res.status === 401) {
    clearSession();
    onAuthLost();
    throw new Error("session-ended");
  }
  if (!res.ok) throw new Error("refresh-unavailable"); // 5xx etc: keep the session
  saveSession(await res.json());
}

export function refreshSession() {
  if (!inflight) {
    const run =
      typeof navigator !== "undefined" && navigator.locks
        ? navigator.locks.request("auth-refresh", doRefresh)
        : doRefresh();
    inflight = run.finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

export async function getAccessToken() {
  if (!accessToken || Date.now() > accessExp - 30_000) await refreshSession();
  return accessToken;
}

export async function refreshAccessToken() {
  accessToken = null;
  accessExp = 0;
  await refreshSession();
  return accessToken;
}

// ------------------------------------------------------------------- apiFetch
// Use this for every protected call. It adds the Bearer token, refreshes when the
// token is about to expire, and retries once if the server answers 401.
// Pass any extra headers (for example x-api-key while the old gateway still needs it).

export async function apiFetch(url, options = {}, retry = true) {
  if (!accessToken || Date.now() > accessExp - 30_000) await refreshSession();

  const baseRequest = new Request(url, options);
  const headers = new Headers(baseRequest.headers);
  headers.set("Authorization", `Bearer ${accessToken}`);
  const res = await fetch(new Request(baseRequest, { headers }));

  // The authorizer throws "Unauthorized" for bad/expired tokens, which API Gateway
  // returns as 401. A 403 is a real "forbidden", so it is not retried.
  if (res.status === 401 && retry) {
    accessToken = null;
    await refreshSession();
    return apiFetch(url, options, false);
  }
  return res;
}

// --------------------------------------------------------------------- logout

export async function logout(allDevices = false) {
  const rt = localStorage.getItem(RT_KEY);
  clearSession();
  if (rt) {
    try {
      await fetch(`${BASE}/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt, allDevices }),
        keepalive: true,
      });
    } catch {} // the local session is already gone; the server copy expires on its own
  }
}